import { Link, router, usePage } from "@inertiajs/react";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { format } from "date-fns";
import {
	Activity,
	Ban,
	Camera,
	Cctv,
	Check,
	Clock3,
	Copy,
	Download,
	Eye,
	FileText,
	MapPinIcon,
	MapPinned,
	MoreHorizontal,
	NotebookPen,
	Stethoscope,
	Trash2,
	User,
	X,
} from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDateContext } from "@/components/providers/date-provider";
import DotBadgeWithLabel from "@/components/shared/dot-badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DataTableColumnHeader } from "@/components/ui/data-table/column-header";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatVisitLine } from "@/lib/appointments/format-visit-line";
import {
	isTimePreference,
	timePreferenceShortWindow,
} from "@/lib/appointments/time-preference";
import { getDotVariantStatus } from "@/lib/appointments/utils";
import {
	cn,
	copyToClipboard,
	downloadFileFromUrl,
	populateQueryParams,
} from "@/lib/utils";
import type { AppointmentIndexGlobalPageProps } from "@/pages/AdminPortal/Appointment/Index";
import type { Appointment } from "@/types/admin-portal/appointment";
import {
	DeleteDocumentDialog,
	type DeleteDocumentTarget,
} from "./delete-document-dialog";
import {
	blockReason,
	DisabledReasonTooltip,
	deleteDocumentReason,
	editSoapReason,
	REASON,
	ReconcileBonusMenuItem,
	useActionMenuRegroup,
	useCanReconcileBonus,
	useCanUpdateSoap,
} from "./details/action-buttons";

const RegistrationNumberCell = memo(({ row }: { row: Row<Appointment> }) => {
	const { url: pageURL } = usePage<AppointmentIndexGlobalPageProps>();
	const { fullUrl } = populateQueryParams(pageURL, {
		view_appointment: row.original.id,
	});

	return (
		<Link
			href={fullUrl}
			preserveScroll
			preserveState
			only={["adminPortal", "flash", "errors", "selectedAppointment"]}
		>
			<span className="font-mono text-sm font-bold text-nowrap text-primary hover:underline">
				{row.original.registrationNumber}
			</span>
		</Link>
	);
});

const AppointmentDateTimeCell = memo(({ row }: { row: Row<Appointment> }) => {
	const { t } = useTranslation("appointments");
	const { locale, tzDate, timeFormatDateFns } = useDateContext();
	const apptDate = row.original.appointmentDateTime;
	const preferredDate = row.original.preferredDate;
	const timePreference = row.original.timePreference;
	const preferredDateLabel = useMemo(
		() =>
			preferredDate
				? format(new Date(preferredDate), "PP", {
						locale,
						in: tzDate,
					})
				: null,
		[preferredDate, locale, tzDate],
	);
	const date = useMemo(
		() =>
			format(new Date(apptDate), "PP", {
				locale,
				in: tzDate,
			}),
		[apptDate, locale, tzDate],
	);
	const time = useMemo(
		() =>
			format(new Date(apptDate), "p", {
				locale,
				in: tzDate,
			}),
		[apptDate, locale, tzDate],
	);
	const full = useMemo(
		() =>
			format(new Date(apptDate), "PPPp", {
				locale,
				in: tzDate,
			}),
		[apptDate, locale, tzDate],
	);

	// No fixed time yet — fall back to the customer's date + time preference,
	// with a status-aware micro-label signalling the negotiation stage.
	if (!apptDate) {
		if (!preferredDateLabel) return "N/A";

		const status = row.original.status;
		const stageLabel =
			status === "pending_mitra_approval"
				? t("schedule_preference.negotiating")
				: status === "pending_therapist_assignment"
					? t("schedule_preference.awaiting_therapist")
					: t("schedule_preference.tentative");

		return (
			<div className="space-y-0.5">
				<p className="font-semibold text-nowrap">{preferredDateLabel}</p>
				<p className="text-[10px] uppercase tracking-wider text-muted-foreground/75 text-nowrap">
					{stageLabel}
				</p>
				{isTimePreference(timePreference) ? (
					<p className="text-nowrap text-muted-foreground italic">
						{timePreferenceShortWindow(timePreference, t, {
							locale,
							timeFormat: timeFormatDateFns,
						})}
					</p>
				) : null}
			</div>
		);
	}

	return (
		<div title={full}>
			<p className="font-semibold text-nowrap">{date}</p>
			<p className="text-nowrap">{time}</p>
		</div>
	);
});

const StatusCell = memo(({ row }: { row: Row<Appointment> }) => {
	const { t } = useTranslation("appointments");
	const status = row.original.status;
	const statusDotVariant = getDotVariantStatus(status);

	if (!status) return "N/A";

	// Fallback for undefined statuses
	const statusText = t(`statuses.${status}`, status);

	return (
		<div className="relative">
			<DotBadgeWithLabel
				size="xs"
				className="relative flex-shrink-0 text-left"
				variant={statusDotVariant}
			>
				<span title={statusText} className="tracking-wide line-clamp-1">
					{statusText}
				</span>
			</DotBadgeWithLabel>
		</div>
	);
});

// The two documents a therapist files after the visit. They are also what locks a completed
// visit out of Update Status and Reschedule (see blockReason in details/action-buttons),
// so a greyed-out action always has its reason visible on the same row.
//
// Both flags ride the list payload — no extra request. Absent means the payload never asked
// for them, which reads the same as "not filed".
const DOCUMENTS = [
	{ key: "soap", label: "SOAP", Icon: FileText },
	{ key: "evidence", label: "Evidence", Icon: Camera },
] as const;

const DocumentsCell = memo(({ row }: { row: Row<Appointment> }) => {
	const { hasSoap, hasEvidence } = row.original;
	const present = { soap: !!hasSoap, evidence: !!hasEvidence };

	return (
		<div className="flex items-center gap-1">
			{DOCUMENTS.map(({ key, label, Icon }) => {
				const filed = present[key];

				return (
					<span
						key={key}
						title={`${label}: ${filed ? "filed" : "not filed"}`}
						className={cn(
							"inline-flex items-center gap-0.5 rounded border px-1 py-0.5",
							filed
								? "border-primary/30 bg-primary/5 text-primary"
								: "border-border text-muted-foreground/40",
						)}
					>
						<Icon className="size-3 flex-shrink-0" aria-hidden="true" />
						{filed ? (
							<Check className="size-3 flex-shrink-0" aria-hidden="true" />
						) : (
							<X className="size-3 flex-shrink-0" aria-hidden="true" />
						)}
						<span className="sr-only">{`${label} ${filed ? "filed" : "not filed"}`}</span>
					</span>
				);
			})}
		</div>
	);
});

const PatientCell = memo(({ row }: { row: Row<Appointment> }) => {
	const { t } = useTranslation("appointments");
	const patient = row.original.patient;
	if (!patient) return <p className="uppercase">N/A</p>;

	const { name, gender, age, contact } = patient;

	return (
		<TooltipProvider delayDuration={0}>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="flex items-center gap-2 max-w-[125px]">
						<Avatar className="text-[10px] border rounded-lg border-border bg-muted size-6">
							<AvatarFallback>
								<User className="flex-shrink-0 size-4 text-muted-foreground/75" />
							</AvatarFallback>
						</Avatar>
						<p title={name} className="uppercase line-clamp-1">
							{name}
						</p>
					</div>
				</TooltipTrigger>
				<TooltipContent className="py-3">
					<ul className="grid grid-cols-2 gap-3 text-xs">
						<li className="grid gap-0.5">
							<span className="font-light">{t("list.gender")}</span>
							<span className="font-medium">{gender || "N/A"}</span>
						</li>
						<li className="grid gap-0.5">
							<span className="font-light">{t("list.age")}</span>
							<span className="font-medium">
								{age || "N/A"} {age && t("list.years")}
							</span>
						</li>
						<li className="grid gap-0.5">
							<span className="font-light">{t("list.contact_name")}</span>
							<span className="font-medium">
								{contact?.contactName || "N/A"}
							</span>
						</li>
						<li className="grid gap-0.5">
							<span className="font-light">{t("list.contact_phone")}</span>
							<span className="font-medium">
								{contact?.contactPhone || "N/A"}
							</span>
						</li>
					</ul>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
});

const TherapistCell = memo(({ row }: { row: Row<Appointment> }) => {
	const { t } = useTranslation("appointments");
	const therapist = row.original.therapist;
	if (!therapist) return <p className="uppercase">N/A</p>;

	const { name, gender, employmentType } = therapist;

	return (
		<TooltipProvider delayDuration={0}>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="flex items-center gap-2 max-w-[125px]">
						<Avatar className="text-[10px] border rounded-lg border-border bg-muted size-6">
							<AvatarFallback>
								<Stethoscope className="flex-shrink-0 size-4 text-muted-foreground/75" />
							</AvatarFallback>
						</Avatar>
						<p title={name} className="uppercase line-clamp-1">
							{name}
						</p>
					</div>
				</TooltipTrigger>
				<TooltipContent className="py-3">
					<ul className="grid gap-3 text-xs">
						<li className="grid gap-0.5">
							<span className="font-light">{t("list.gender")}</span>
							<span className="font-medium">{gender || "N/A"}</span>
						</li>
						<li className="grid gap-0.5">
							<span className="font-light">{t("list.emp_type")}</span>
							<span className="font-medium">{employmentType || "N/A"}</span>
						</li>
					</ul>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
});

const ServiceCell = memo(({ row }: { row: Row<Appointment> }) => {
	const { service, package: servicePackage } = row.original;
	const serviceName = service?.name?.replaceAll("_", " ") || null;
	const packageName = servicePackage?.name || null;
	const visitLabel = servicePackage
		? `${servicePackage.numberOfVisit} visit(s)`
		: null;

	if (!serviceName) return null;

	return (
		<div title={`${serviceName} | ${packageName} - ${visitLabel}`}>
			<p className="font-semibold line-clamp-1">{serviceName}</p>
			{packageName ? (
				<p className="line-clamp-1">
					<span>{packageName}</span>
					<span className="mx-0.5">-</span>
					<span className="italic">{visitLabel}</span>
				</p>
			) : null}
		</div>
	);
});

const LocationCell = memo(({ row }: { row: Row<Appointment> }) => {
	const location = row.original.location;
	if (!location) return <p className="font-semibold uppercase">N/A</p>;

	const { city, state, country } = location;

	return (
		<div title={city}>
			<p className="font-semibold line-clamp-1">{city}</p>
			<p className="line-clamp-1">
				{state}, {country}
			</p>
		</div>
	);
});

const VisitNoCell = memo(({ row }: { row: Row<Appointment> }) => {
	const visitProgress = row.original.visitProgress || "N/A";

	return (
		<div>
			<p className="line-clamp-1 font-semibold">Visit {visitProgress}</p>
		</div>
	);
});

const CreatedAtCell = memo(({ row }: { row: Row<Appointment> }) => {
	const { locale, tzDate } = useDateContext();
	const createdAt = row.original.createdAt;

	const date = useMemo(
		() =>
			createdAt
				? format(new Date(createdAt), "PP", {
						locale,
						in: tzDate,
					})
				: "N/A",
		[createdAt, locale, tzDate],
	);

	const time = useMemo(
		() =>
			createdAt
				? format(new Date(createdAt), "p", {
						locale,
						in: tzDate,
					})
				: "N/A",
		[createdAt, locale, tzDate],
	);

	const full = useMemo(
		() =>
			createdAt
				? format(new Date(createdAt), "PPPp", {
						locale,
						in: tzDate,
					})
				: "N/A",
		[createdAt, locale, tzDate],
	);

	if (!createdAt) return "N/A";

	return (
		<div title={full}>
			<p className="font-semibold text-nowrap">{date}</p>
			<p className="text-nowrap">{time}</p>
		</div>
	);
});

const useCopyRegistrationNumber = () => {
	const handleCopy = useCallback(async (value: string, label: string) => {
		// copyToClipboard falls back to execCommand when the async Clipboard API
		// is unavailable (e.g. non-HTTPS origins on the LAN).
		const ok = await copyToClipboard(value);
		if (ok) {
			toast.success(`${label} copied to clipboard`);
		} else {
			toast.error(`Failed to copy ${label.toLowerCase()}`);
		}
	}, []);

	return handleCopy;
};

const ActionsCell = memo(({ row }: { row: Row<Appointment> }) => {
	const appointment = row.original;
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentIndexGlobalPageProps>();
	const copyRegistrationNumber = useCopyRegistrationNumber();
	const { t: tappt } = useTranslation("appointments");
	const { locale, tzDate, timeFormatDateFns } = useDateContext();

	const canUpdateSoap = useCanUpdateSoap();
	const canReconcileBonus = useCanReconcileBonus();
	const regroup = useActionMenuRegroup();
	const [deleting, setDeleting] = useState<DeleteDocumentTarget | null>(null);
	const blocked = useMemo(
		() => ({
			updateStatus: blockReason.updateStatus(appointment),
			cancel: blockReason.cancel(appointment),
			reschedule: blockReason.reschedule(appointment),
			updateAddress: blockReason.updateAddress(appointment),
			editMedicalRecord: blockReason.editMedicalRecord(appointment),
			editSoap: editSoapReason(appointment, canUpdateSoap),
			// The final report closes the package, so only its last visit has one.
			downloadFinalSoap:
				appointment.visitNumber === appointment.totalPackageVisits
					? null
					: REASON.notLastVisit,
			downloadInvoice: appointment.invoiceNumber ? null : REASON.noInvoice,
		}),
		[appointment, canUpdateSoap],
	);

	const onCopyRegNumber = useCallback(() => {
		copyRegistrationNumber(
			appointment.registrationNumber,
			"Registration number",
		);
	}, [appointment.registrationNumber, copyRegistrationNumber]);

	const onCopyPatientNumber = useCallback(() => {
		if (!appointment.patient?.patientNumber) {
			toast.error("Patient number unavailable");
			return;
		}
		copyRegistrationNumber(appointment.patient.patientNumber, "Patient number");
	}, [appointment.patient?.patientNumber, copyRegistrationNumber]);

	const onCopyTherapistNumber = useCallback(() => {
		if (!appointment.therapist?.registrationNumber) {
			toast.error("Therapist registration number unavailable");
			return;
		}
		copyRegistrationNumber(
			appointment.therapist.registrationNumber,
			"Therapist registration number",
		);
	}, [appointment.therapist?.registrationNumber, copyRegistrationNumber]);

	const onCopyInvoiceNumber = useCallback(() => {
		if (!appointment.invoiceNumber) {
			toast.error("Invoice number unavailable");
			return;
		}
		copyRegistrationNumber(appointment.invoiceNumber, "Invoice number");
	}, [appointment.invoiceNumber, copyRegistrationNumber]);

	const onCopyInvoiceUrl = useCallback(() => {
		if (!appointment.invoiceUrl) {
			toast.error("Invoice URL unavailable");
			return;
		}
		copyRegistrationNumber(appointment.invoiceUrl, "Invoice URL");
	}, [appointment.invoiceUrl, copyRegistrationNumber]);

	// Copies this visit as one line, e.g.
	// "Visit 1: Selasa, 28 Juli 2026 (16:00 WIB) - FT Intan Rahmawati".
	const onCopyVisitLine = useCallback(() => {
		const text = formatVisitLine(
			{
				visitNumber: appointment.visitNumber,
				appointmentDateTime: appointment.appointmentDateTime,
				preferredDate: appointment.preferredDate,
				timePreference: appointment.timePreference,
				therapistName: appointment.therapist?.name ?? null,
				serviceCode: appointment.service?.code ?? null,
			},
			{ locale, tzDate, timeFormat: timeFormatDateFns, t: tappt },
		);
		copyRegistrationNumber(text, "Visit schedule");
	}, [
		appointment.visitNumber,
		appointment.appointmentDateTime,
		appointment.preferredDate,
		appointment.timePreference,
		appointment.therapist?.name,
		appointment.service?.code,
		locale,
		tzDate,
		timeFormatDateFns,
		tappt,
		copyRegistrationNumber,
	]);

	const onDownloadSoapReport = useCallback(async () => {
		const loadingToast = toast.loading("Preparing SOAP report...");
		try {
			const url = `/admin-portal/appointments/${appointment.id}/soap-report`;
			await downloadFileFromUrl(url, "soap-report.pdf");
			toast.success("SOAP report downloaded", { id: loadingToast });
		} catch (error) {
			const message = "Failed to download SOAP report";
			console.error(`${message}: ${error}`);
			toast.error(message, { id: loadingToast });
		}
	}, [appointment.id]);

	const onDownloadSoapFinal = useCallback(async () => {
		const loadingToast = toast.loading("Preparing Final SOAP report...");
		try {
			const url = `/admin-portal/appointments/${appointment.id}/soap-report-final`;
			await downloadFileFromUrl(url, "final-report.pdf");
			toast.success("Final SOAP report downloaded", { id: loadingToast });
		} catch (error) {
			const message = "Failed to download Final SOAP report";
			console.error(`${message}: ${error}`);
			toast.error(message, { id: loadingToast });
		}
	}, [appointment.id]);

	const onDownloadInvoice = useCallback(async () => {
		const loadingToast = toast.loading("Preparing Invoice...");
		try {
			const url = `/admin-portal/appointments/${appointment.id}/invoice`;
			await downloadFileFromUrl(url, "invoice.pdf");
			toast.success("Invoice downloaded", { id: loadingToast });
		} catch (error) {
			const message = "Failed to download Invoice";
			console.error(`${message}: ${error}`);
			toast.error(message, { id: loadingToast });
		}
	}, [appointment.id]);

	const routeTo = useMemo(
		() => ({
			viewDetails: (id: string) => {
				const { fullUrl } = populateQueryParams(pageURL, {
					view_appointment: id,
				});
				router.get(
					fullUrl,
					{},
					{
						only: ["adminPortal", "flash", "errors", "selectedAppointment"],
						preserveScroll: true,
						preserveState: true,
					},
				);
			},
			cancel: (id: string) =>
				router.get(
					pageURL,
					{ cancel: id },
					{
						only: ["adminPortal", "flash", "errors", "selectedAppointment"],
						preserveScroll: true,
						preserveState: true,
					},
				),
			updatePic: (id: string) =>
				router.get(
					pageURL,
					{ update_pic: id },
					{
						only: [
							"adminPortal",
							"flash",
							"errors",
							"selectedAppointment",
							"optionsData",
						],
						preserveScroll: true,
						preserveState: true,
					},
				),
			updateStatus: (id: string) =>
				router.get(
					pageURL,
					{ update_status: id },
					{
						only: [
							"adminPortal",
							"flash",
							"errors",
							"selectedAppointment",
							"optionsData",
						],
						preserveScroll: true,
						preserveState: true,
					},
				),
			updateAddress: (id: string) =>
				router.get(
					pageURL,
					{ update_address: id },
					{
						only: [
							"adminPortal",
							"flash",
							"errors",
							"selectedAppointment",
							"filterOptionsData",
						],
						preserveScroll: true,
						preserveState: true,
					},
				),
			editMedicalRecord: (id: string) =>
				router.get(
					pageURL,
					{ edit_medical_record: id },
					{
						only: ["adminPortal", "flash", "errors", "selectedAppointment"],
						preserveScroll: true,
						preserveState: true,
					},
				),
			editSoap: (id: string) =>
				router.get(
					pageURL,
					{ edit_soap: id },
					{
						only: ["adminPortal", "flash", "errors", "selectedAppointment"],
						preserveScroll: true,
						preserveState: true,
					},
				),
			reschedule: (id: string) => {
				const url = `${globalProps.adminPortal.router.adminPortal.appointment.index}/${id}/reschedule`;
				router.visit(url);
			},
		}),
		[pageURL, globalProps.adminPortal.router.adminPortal.appointment.index],
	);

	// * bypass this because it's for our admin internal uses
	const canShowActions = true || appointment.status !== "cancelled";

	// Built once and placed by ACTION_MENU_REGROUP: inline (the layout admins know) or
	// inside one Download submenu, where the repeated "Download" prefix drops.
	const downloadItems = (
		<>
			<DropdownMenuItem onSelect={onDownloadSoapReport}>
				<Download className="opacity-60" aria-hidden="true" />
				{regroup ? "SOAP Report" : "Download SOAP Report"}
			</DropdownMenuItem>

			<DisabledReasonTooltip reason={blocked.downloadFinalSoap} side="left">
				<DropdownMenuItem
					onSelect={onDownloadSoapFinal}
					disabled={!!blocked.downloadFinalSoap}
				>
					<Download className="opacity-60" aria-hidden="true" />
					{regroup ? "Final SOAP" : "Download Final SOAP"}
				</DropdownMenuItem>
			</DisabledReasonTooltip>

			<DisabledReasonTooltip reason={blocked.downloadInvoice} side="left">
				<DropdownMenuItem
					onSelect={onDownloadInvoice}
					disabled={!!blocked.downloadInvoice}
				>
					<Download className="opacity-60" aria-hidden="true" />
					{regroup ? "Invoice" : "Download Invoice"}
				</DropdownMenuItem>
			</DisabledReasonTooltip>
		</>
	);

	return (
		<DropdownMenu>
			{/* Lives outside the menu content, which unmounts when the menu closes. */}
			<DeleteDocumentDialog
				target={deleting}
				onClose={() => setDeleting(null)}
				reloadOnly={[
					"adminPortal",
					"flash",
					"errors",
					"selectedAppointment",
					"appointments",
				]}
			/>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon">
					<span className="sr-only">Open menu</span>
					<MoreHorizontal className="w-4 h-4" />
				</Button>
			</DropdownMenuTrigger>

			<DropdownMenuContent align="end">
				<DropdownMenuGroup>
					<DropdownMenuItem
						onSelect={() => routeTo.viewDetails(String(appointment.id))}
					>
						<Eye className="opacity-60" aria-hidden="true" />
						View Details
					</DropdownMenuItem>

					<DropdownMenuItem
						onSelect={() => {
							window.open(
								`https://www.google.com/maps/search/?api=1&query=${appointment?.visitAddress?.coordinates.x},${appointment.visitAddress?.coordinates.y}`,
							);
						}}
						disabled={!appointment?.visitAddress?.coordinates}
					>
						<MapPinIcon className="opacity-60" aria-hidden="true" />
						View on Google Maps
					</DropdownMenuItem>

					<DropdownMenuSub>
						<DropdownMenuSubTrigger>
							<Copy className="opacity-60" aria-hidden="true" />
							Copy
						</DropdownMenuSubTrigger>

						<DropdownMenuPortal>
							<DropdownMenuSubContent>
								<DropdownMenuItem
									onSelect={onCopyInvoiceUrl}
									disabled={!appointment.invoiceUrl}
								>
									Invoice URL
								</DropdownMenuItem>
								<DropdownMenuItem
									onSelect={onCopyRegNumber}
									disabled={!appointment.registrationNumber}
								>
									Appt Reg. Number
								</DropdownMenuItem>
								<DropdownMenuItem
									onSelect={onCopyInvoiceNumber}
									disabled={!appointment.invoiceNumber}
								>
									No. Invoice
								</DropdownMenuItem>
								<DropdownMenuItem
									onSelect={onCopyPatientNumber}
									disabled={!appointment.patientId}
								>
									Patient ID
								</DropdownMenuItem>
								<DropdownMenuItem
									onSelect={onCopyTherapistNumber}
									disabled={!appointment.therapistId}
								>
									Therapist ID
								</DropdownMenuItem>
								<DropdownMenuItem onSelect={onCopyVisitLine}>
									Visit Schedule
								</DropdownMenuItem>
							</DropdownMenuSubContent>
						</DropdownMenuPortal>
					</DropdownMenuSub>

					{regroup ? (
						<DropdownMenuSub>
							<DropdownMenuSubTrigger>
								<Download className="opacity-60" aria-hidden="true" />
								Download
							</DropdownMenuSubTrigger>
							<DropdownMenuPortal>
								<DropdownMenuSubContent>{downloadItems}</DropdownMenuSubContent>
							</DropdownMenuPortal>
						</DropdownMenuSub>
					) : (
						downloadItems
					)}
				</DropdownMenuGroup>

				<DropdownMenuSeparator />

				<DropdownMenuGroup>
					{canShowActions && (
						<>
							<DisabledReasonTooltip reason={blocked.reschedule} side="left">
								<DropdownMenuItem
									onSelect={() => routeTo.reschedule(String(appointment.id))}
									disabled={!!blocked.reschedule}
								>
									<Clock3 className="opacity-60" aria-hidden="true" />
									Reschedule
								</DropdownMenuItem>
							</DisabledReasonTooltip>

							{/* Kept visible when locked — an absent row reads as a missing
							    feature, a greyed one reads as "this visit is done". */}
							<DisabledReasonTooltip reason={blocked.updateStatus} side="left">
								<DropdownMenuItem
									onSelect={() => routeTo.updateStatus(String(appointment.id))}
									disabled={!!blocked.updateStatus}
								>
									<Activity className="opacity-60" aria-hidden="true" />
									Update Status
								</DropdownMenuItem>
							</DisabledReasonTooltip>
						</>
					)}

					<DropdownMenuItem
						onSelect={() => routeTo.updatePic(String(appointment.id))}
					>
						<Cctv className="opacity-60" aria-hidden="true" />
						Update PIC
					</DropdownMenuItem>

					<DisabledReasonTooltip reason={blocked.updateAddress} side="left">
						<DropdownMenuItem
							onSelect={() => routeTo.updateAddress(String(appointment.id))}
							disabled={!!blocked.updateAddress}
						>
							<MapPinned className="opacity-60" aria-hidden="true" />
							Update Address
						</DropdownMenuItem>
					</DisabledReasonTooltip>

					<DisabledReasonTooltip reason={blocked.editMedicalRecord} side="left">
						<DropdownMenuItem
							onSelect={() => routeTo.editMedicalRecord(String(appointment.id))}
							disabled={!!blocked.editMedicalRecord}
						>
							<Stethoscope className="opacity-60" aria-hidden="true" />
							Update Medical Record
						</DropdownMenuItem>
					</DisabledReasonTooltip>

					{/* Greyed out rather than dropped when the visit has no SOAP: upstream
					    answers 404 for one, and a missing row would read as a missing feature. */}
					<DisabledReasonTooltip reason={blocked.editSoap} side="left">
						<DropdownMenuItem
							onSelect={() => routeTo.editSoap(String(appointment.id))}
							disabled={!!blocked.editSoap}
						>
							<NotebookPen className="opacity-60" aria-hidden="true" />
							Update SOAP
						</DropdownMenuItem>
					</DisabledReasonTooltip>

					{canReconcileBonus && (
						<ReconcileBonusMenuItem orderId={appointment.reconcilableOrderId} />
					)}
				</DropdownMenuGroup>

				{canShowActions && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuGroup>
							{/* Same whitelist as Update SOAP; each item needs the document it
							    would delete, since upstream answers 404 without one. */}
							{(
								[
									{
										kind: "soap",
										label: tappt("button.delete_soap"),
										has: appointment.hasSoap ?? !!appointment.soap,
									},
									{
										kind: "evidence",
										label: tappt("button.delete_evidence"),
										has: appointment.hasEvidence ?? !!appointment.evidence,
									},
								] as const
							).map(({ kind, label, has }) => (
								<DisabledReasonTooltip
									key={kind}
									reason={deleteDocumentReason(kind, has, canUpdateSoap)}
									side="left"
								>
									<DropdownMenuItem
										className="text-destructive focus:text-destructive"
										disabled={!!deleteDocumentReason(kind, has, canUpdateSoap)}
										onSelect={() =>
											setDeleting({
												appointmentId: String(appointment.id),
												kind,
												visitNumber: appointment.visitNumber,
												totalVisits: appointment.totalPackageVisits,
												therapistName: appointment.therapist?.name,
												hasOtherDocument:
													kind === "soap"
														? (appointment.hasEvidence ??
															!!appointment.evidence)
														: (appointment.hasSoap ?? !!appointment.soap),
											})
										}
									>
										<Trash2 className="opacity-60" aria-hidden="true" />
										{label}
									</DropdownMenuItem>
								</DisabledReasonTooltip>
							))}

							{/* Greyed out rather than dropped when the visit is locked, matching
							    Reschedule and Update Status above — a missing row reads as a
							    missing feature, a greyed one reads as "this visit is done". */}
							<DisabledReasonTooltip reason={blocked.cancel} side="left">
								<DropdownMenuItem
									className="text-destructive focus:text-destructive"
									onSelect={() => routeTo.cancel(String(appointment.id))}
									disabled={!!blocked.cancel}
								>
									<Ban className="opacity-60" aria-hidden="true" />
									Cancel Booking
								</DropdownMenuItem>
							</DisabledReasonTooltip>
						</DropdownMenuGroup>
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
});

// The columns the toolbar's Columns menu can switch off, in table order. Reg. No. is the
// sticky identity column and the actions cell has no header, so both stay put and are the
// only two marked enableHiding: false below.
//
// Labels live here rather than inline in the headers so the menu and the header can't
// drift apart. They stay untranslated, like the headers themselves.
export const HIDEABLE_COLUMNS = [
	{ id: "visitNumber", label: "Visit No." },
	{ id: "appointmentDateTime", label: "Visit Date Time" },
	{ id: "status", label: "Status" },
	{ id: "documents", label: "Docs" },
	{ id: "patient", label: "Patient" },
	{ id: "therapist", label: "Therapist" },
	{ id: "service", label: "Service" },
	{ id: "location", label: "Location" },
	{ id: "createdAt", label: "Created At" },
] as const;

const label = (id: (typeof HIDEABLE_COLUMNS)[number]["id"]) =>
	HIDEABLE_COLUMNS.find((column) => column.id === id)?.label ?? id;

const getColumns = (): ColumnDef<Appointment>[] => [
	{
		accessorKey: "registrationNumber",
		enableHiding: false,
		header: "Reg. No.",
		cell: ({ row }) => <RegistrationNumberCell row={row} />,
		size: 120,
		minSize: 100,
		maxSize: 150,
	},
	{
		id: "visitNumber",
		accessorKey: "visitNumber",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={label("visitNumber")} />
		),
		cell: ({ row }) => <VisitNoCell row={row} />,
	},
	{
		id: "appointmentDateTime",
		accessorKey: "appointmentDateTime",
		header: ({ column }) => (
			<DataTableColumnHeader
				column={column}
				title={label("appointmentDateTime")}
			/>
		),
		cell: ({ row }) => <AppointmentDateTimeCell row={row} />,
	},
	{
		id: "status",
		accessorKey: "status",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={label("status")} />
		),
		cell: ({ row }) => <StatusCell row={row} />,
	},
	{
		id: "documents",
		// Sorting is client-side here while the list sorts on the server, so it stays off.
		enableSorting: false,
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={label("documents")} />
		),
		cell: ({ row }) => <DocumentsCell row={row} />,
		size: 80,
		minSize: 70,
		maxSize: 100,
	},
	{
		id: "patient",
		accessorKey: "patient.name",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={label("patient")} />
		),
		cell: ({ row }) => <PatientCell row={row} />,
	},
	{
		id: "therapist",
		accessorKey: "therapist.name",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={label("therapist")} />
		),
		cell: ({ row }) => <TherapistCell row={row} />,
	},
	{
		id: "service",
		accessorKey: "service.name",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={label("service")} />
		),
		cell: ({ row }) => <ServiceCell row={row} />,
	},
	{
		id: "location",
		accessorKey: "location.city",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={label("location")} />
		),
		cell: ({ row }) => <LocationCell row={row} />,
	},
	{
		id: "createdAt",
		accessorKey: "createdAt",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={label("createdAt")} />
		),
		cell: ({ row }) => <CreatedAtCell row={row} />,
	},
	{
		id: "actions",
		enableHiding: false,
		cell: ({ row }) => (
			<div className="flex justify-end items-center">
				<ActionsCell row={row} />
			</div>
		),
	},
];

export default getColumns;
